import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    MapContainer,
    TileLayer,
    GeoJSON,
    LayersControl,
    useMap,
} from "react-leaflet";
import { Box } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import "leaflet/dist/leaflet.css";
import { DOMParser } from "@xmldom/xmldom";
import { kml } from "@tmcw/togeojson";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";
import { useForm } from "react-hook-form";
import "./Comparison.css";
import { MapHomeControl } from "../../../common/MapControls/MapControls";
// Note: No need for the default marker icon fixes here, as we are using custom icons.

const INITIAL_VIEW_STATE = {
    longitude: 125.7275,
    latitude: -8.8742,
    zoom: 8,
};

const InfoButton = ({ onClick }) => {
    // Leaflet's own drag/click handling swallows clicks on overlay controls
    // unless propagation to the map is explicitly disabled on the DOM node.
    const btnRef = useCallback((node) => {
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);
    return (
        <div
            ref={btnRef}
            style={{
                position: "absolute",
                bottom: "20px",
                left: "12px",
                padding: "8px",
                borderRadius: "8px",
                fontSize: "15px",
                cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
                zIndex: 1000,
            }}
            onClick={onClick}
        >
            <FaCircleInfo />
        </div>
    );
};

const MapViewAdjuster = ({ features }) => {
    const map = useMap();

    useEffect(() => {
        if (features.length > 0) {
            const allFeatures = features.flatMap((kmlFile) => kmlFile.features);
            if (allFeatures.length > 0) {
                const bounds = L.latLngBounds(
                    allFeatures
                        .map((feature) => {
                            if (feature.geometry.type === "Point") {
                                return [
                                    feature.geometry.coordinates[1],
                                    feature.geometry.coordinates[0],
                                ];
                            } else if (feature.geometry.type === "Polygon") {
                                return feature.geometry.coordinates[0].map((coord) => [
                                    coord[1],
                                    coord[0],
                                ]);
                            } else if (feature.geometry.type === "LineString") {
                                return feature.geometry.coordinates.map((coord) => [
                                    coord[1],
                                    coord[0],
                                ]);
                            }
                            return null;
                        })
                        .flat()
                );
                map.fitBounds(bounds, { padding: [50, 50] });
            }
        }
    }, [features, map]);

    return null;
};

// Moved outside the component to fix the useEffect dependency warning
const convertKMLColor = (kmlColor) => {
    if (!kmlColor || kmlColor.length !== 8) return "#000000";
    const alpha = parseInt(kmlColor.slice(0, 2), 16) / 255;
    const blue = kmlColor.slice(2, 4);
    const green = kmlColor.slice(4, 6);
    const red = kmlColor.slice(6, 8);
    return `rgba(${parseInt(red, 16)}, ${parseInt(green, 16)}, ${parseInt(blue, 16)}, ${alpha.toFixed(2)})`;
};

const fetchKmlData = async (kmlUrl) => {
    try {
        const response = await fetch(kmlUrl);
        const data = await response.text();
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(data, "text/xml");
        const convertedGeoJSON = kml(kmlDoc);

        const extractedStyles = Array.from(kmlDoc.getElementsByTagName("Style")).map((style) => {
            const id = style.getAttribute("id");
            const lineColor = style.getElementsByTagName("color")[0]?.textContent || "";
            const polyColor = style.getElementsByTagName("color")[1]?.textContent || "";
            return {
                id,
                borderColor: convertKMLColor(lineColor),
                fillColor: convertKMLColor(polyColor),
            };
        });

        const iconStyles = Array.from(kmlDoc.getElementsByTagName("Style")).flatMap((style) => {
            const id = style.getAttribute("id");
            const iconHref = style.getElementsByTagName("href")[0]?.textContent || null;
            return iconHref ? { id, iconUrl: iconHref } : [];
        });

        const screenOverlays = kmlDoc.getElementsByTagName("ScreenOverlay");
        const legends = [];
        for (let i = 0; i < screenOverlays.length; i++) {
            const iconElement = screenOverlays[i].getElementsByTagName("Icon")[0];
            if (iconElement) {
                legends.push(iconElement.textContent.trim());
            }
        }

        return {
            geoJson: convertedGeoJSON,
            styles: extractedStyles,
            icons: iconStyles,
            legends: legends,
        };
    } catch (error) {
        console.error("Error loading KML file:", kmlUrl, error);
        return null;
    }
};

const Comparison = () => {
    const [kmlData, setKmlData] = useState([]);
    const [styles, setStyles] = useState([]);
    const [icons, setIcons] = useState([]);
    const [legendImageUrls, setLegendImageUrls] = useState([]);
    const [municipalityData, setMunicipalityData] = useState(null);
    const [subMunicipalityData, setSubMunicipalityData] = useState(null);
    const [sucoData, setSucoData] = useState(null);
    const [info, setInfo] = useState(false);
    const infoRef = useRef(null);
    const infoPanelRef = useCallback((node) => {
        infoRef.current = node;
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);
    useEffect(() => {
        if (!info) return;
        const handleClickOutside = (e) => {
            if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [info]);
    const [showKmlLayers, setShowKmlLayers] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid },
    } = useForm({ mode: "onChange" });

    const uniqueCycloneNames = ["Cyclone A", "Cyclone B", "Cyclone C"];
    const availableDates = ["2025-08-10", "2025-08-11", "2025-08-12"];
    const selectedCycloneName = watch("cycloneName");

    const onSubmit = (data) => {
        console.log("Filter submitted:", data);
        setShowKmlLayers(true);
    };

    useEffect(() => {
        const kml1Url = "/DATA/Cyclone/TSR/202126S_1_cat1prob_2021030512.kml";
        const kml2Url = "/DATA/Cyclone/TSR/202126S_5_TSprob_2021030512.kml";
        const kmlLabels = ["TSR", "JTWC"];

        const loadKmlData = async () => {
            const [kml1, kml2] = await Promise.all([
                fetchKmlData(kml1Url),
                fetchKmlData(kml2Url),
            ]);

            const newKmlData = [];
            const newStyles = [];
            const newIcons = [];
            const newLegends = [];

            if (kml1) {
                newKmlData.push({ ...kml1.geoJson, label: kmlLabels[0] });
                newStyles.push(...kml1.styles);
                newIcons.push(...kml1.icons);
                newLegends.push(...kml1.legends);
            }
            if (kml2) {
                newKmlData.push({ ...kml2.geoJson, label: kmlLabels[1] });
                newStyles.push(...kml2.styles);
                newIcons.push(...kml2.icons);
                newLegends.push(...kml2.legends);
            }

            setKmlData(newKmlData);
            setStyles(newStyles);
            setIcons(newIcons);
            setLegendImageUrls(newLegends);
        };

        const loadBoundaryData = async () => {
            try {
                const urls = [
                    "/DATA/OSMbased_adm1_boundary.json",
                    "/DATA/OSMbased_adm2_boundary.json",
                    "/DATA/OSMbased_adm3_boundary.json",
                ];
                const responses = await Promise.all(urls.map((url) => fetch(url)));
                const data = await Promise.all(responses.map((res) => res.json()));

                setMunicipalityData(data[0]);
                setSubMunicipalityData(data[1]);
                setSucoData(data[2]);
            } catch (error) {
                console.error("Error loading boundary data:", error);
            }
        };

        loadKmlData();
        loadBoundaryData();
    }, []); // Dependency array is now empty because fetchKmlData is outside

    const renderGeoJSONLayer = (geoJsonData, fileIndex) => {
        if (!geoJsonData || !geoJsonData.features) return null;

        const onEachFeature = (feature, layer) => {
            if (feature.properties && feature.properties.name) {
                layer.bindPopup(feature.properties.name);
            }
        };

        const pointToLayer = (feature, latlng) => {
            if (feature.geometry.type === "Point") {
                const iconStyle = icons.find(icon => feature.properties.styleUrl?.includes(icon.id));
                
                if (iconStyle) {
                    const icon = L.icon({
                        iconUrl: iconStyle.iconUrl,
                        iconSize: [32, 32]
                    });
                    return L.marker(latlng, { icon });
                }
                // Fallback to a default marker if a custom icon is not found
                return L.marker(latlng);
            }
            return null;
        };

        return (
            <GeoJSON
                key={`kml-geojson-${fileIndex}`}
                data={geoJsonData}
                style={(feature) => {
                    const style = styles.find(s => feature.properties.styleUrl?.includes(s.id));
                    if (style) {
                        return {
                            color: style.borderColor,
                            weight: 2,
                            opacity: 0.8,
                            fillColor: style.fillColor,
                            fillOpacity: 0.4,
                        };
                    }
                    return {};
                }}
                onEachFeature={onEachFeature}
                pointToLayer={pointToLayer}
            />
        );
    };

    return (
        <div className="cyclone-map-container">
            {/* Filter Bar - outside map, similar to landslide/forecast */}
            <Box
                sx={{
                    p: 2.5,
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
                    border: "1px solid #e8ecf1",
                    marginBottom: "16px",
                    overflow: "visible",
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <form onSubmit={handleSubmit(onSubmit)} style={{ margin: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        {/* Filter Header */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 22 }} />
                            <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>Filter</span>
                        </Box>

                        {/* Divider */}
                        <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

                        {/* Cyclone Select */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Cyclone:</span>
                            <select
                                {...register("cycloneName", {
                                    required: "Please select a Cyclone",
                                })}
                                style={{
                                    width: "180px",
                                    fontSize: "13px",
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #ced4da",
                                    outline: "none",
                                    backgroundColor: "#fff",
                                }}
                            >
                                <option value="">Select Cyclone</option>
                                {uniqueCycloneNames.map((name, index) => (
                                    <option key={index} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                            {errors.cycloneName && (
                                <span style={{ fontSize: "11px", color: "#dc3545" }}>
                                    {errors.cycloneName.message}
                                </span>
                            )}
                        </Box>

                        {/* Divider */}
                        <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

                        {/* Date Select */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Date:</span>
                            <select
                                {...register("selectedDate", {
                                    required: "Please select a Date",
                                })}
                                disabled={!selectedCycloneName}
                                style={{
                                    width: "180px",
                                    fontSize: "13px",
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #ced4da",
                                    outline: "none",
                                    backgroundColor: !selectedCycloneName ? "#e9ecef" : "#fff",
                                }}
                            >
                                <option value="">Select Date</option>
                                {availableDates.map((date, index) => (
                                    <option key={index} value={date}>
                                        {date}
                                    </option>
                                ))}
                            </select>
                            {errors.selectedDate && (
                                <span style={{ fontSize: "11px", color: "#dc3545" }}>
                                    {errors.selectedDate.message}
                                </span>
                            )}
                        </Box>

                        {/* Divider */}
                        <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={!isValid}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "7px 20px",
                                backgroundColor: !isValid ? "#90caf9" : "#0d6efd",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: "8px",
                                fontSize: "13px",
                                fontWeight: 600,
                                cursor: !isValid ? "not-allowed" : "pointer",
                                boxShadow: "0 2px 6px rgba(13, 110, 253, 0.3)",
                                transition: "all 0.2s ease",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Submit
                        </button>
                    </Box>
                </form>
            </Box>

            <MapContainer
                center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
                zoom={INITIAL_VIEW_STATE.zoom}
                style={{ width: "100%", height: "670px" }}
            >
              <MapHomeControl />
                {info && (
                    <div
                        ref={infoPanelRef}
                        style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
                    >
                        Active cyclone track information will be displayed here. If no active track is available, this page will indicate that there are currently no active cyclone warnings.
                    </div>
                )}
                <InfoButton onClick={() => setInfo(!info)} />
                {showKmlLayers && kmlData.length > 0 && <MapViewAdjuster features={kmlData} />}

                <LayersControl position="topright">
                    <LayersControl.BaseLayer name="Satellite" checked>
                        <TileLayer
                            url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                            attribution='Map data © Tropical Storm Risk (TSR) & JTWC'
                        />
                    </LayersControl.BaseLayer>
                    {municipalityData && (
                        <LayersControl.Overlay name="Municipality" checked>
                            <GeoJSON
                                data={municipalityData}
                                style={{
                                    color: "#333333",
                                    weight: 1,
                                    opacity: 1,
                                    fillColor: "#87CEFA",
                                    fillOpacity: 0.3,
                                }}
                            />
                        </LayersControl.Overlay>
                    )}
                    {subMunicipalityData && (
                        <LayersControl.Overlay name="Sub-Municipality">
                            <GeoJSON
                                data={subMunicipalityData}
                                style={{
                                    color: "#333333",
                                    weight: 1,
                                    opacity: 1,
                                    fillColor: "#ADD8E6",
                                    fillOpacity: 0.3,
                                }}
                            />
                        </LayersControl.Overlay>
                    )}
                    {sucoData && (
                        <LayersControl.Overlay name="Suco">
                            <GeoJSON
                                data={sucoData}
                                style={{
                                    color: "#333333",
                                    weight: 1,
                                    opacity: 1,
                                    fillColor: "#B0E0E6",
                                    fillOpacity: 0.3,
                                }}
                            />
                        </LayersControl.Overlay>
                    )}
                    {showKmlLayers && kmlData.length > 0 && (
                        <>
                            <LayersControl.Overlay name="TSR" checked={true}>
                                {renderGeoJSONLayer(kmlData[0], 0)}
                            </LayersControl.Overlay>
                            <LayersControl.Overlay name="JTWC" checked={true}>
                                {renderGeoJSONLayer(kmlData[1], 1)}
                            </LayersControl.Overlay>
                        </>
                    )}
                </LayersControl>
                {/* <div className="source-note">
                    Source: Tropical Storm Risk (TSR) & JTWC
                </div> */}
                {legendImageUrls[0] && (
                    <div className="map-legends intensity-legend">
                        <img src={legendImageUrls[0]} alt="Intensity Legend" />
                    </div>
                )}
                {legendImageUrls[1] && (
                    <div className="map-legends normal-legend">
                        <img src={legendImageUrls[1]} alt="Normal Legend" />
                    </div>
                )}
            </MapContainer>
        </div>
    );
};

export default Comparison;